import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { cfpBaseUrl, wfsBaseUrl } from '../../network/urls';

const CACHE_EXPIRY_HOURS = 24;
const divisionGeoJsonMemoryCache = new Map();

const getCachedDivisionGeoJson = (officeCode) => {
    const entry = divisionGeoJsonMemoryCache.get(String(officeCode));
    if (!entry) return null;
    if (Date.now() - entry.timestamp >= CACHE_EXPIRY_HOURS * 60 * 60 * 1000) {
        divisionGeoJsonMemoryCache.delete(String(officeCode));
        return null;
    }
    return entry.data;
};

const setCachedDivisionGeoJson = (officeCode, data) => {
    divisionGeoJsonMemoryCache.set(String(officeCode), {
        data,
        timestamp: Date.now(),
    });
};

const initialState = {
    data: null,
    isLoading: false,
    error: null,
}

// Define an async thunk for fetching paginated data
export const fetchDivisionGeoJson = createAsyncThunk(
    'complaints/fetchDivisionGeoJsonArray',
    async (subDivisionCode, { getState }) => {
        const { auth } = getState();
        const { wmsPrivateToken, code, hierarchy, token } = auth;

        const officeCodeWithDiv = subDivisionCode.subDivisionCode + 'div';

        // Check cache first
        const cachedData = getCachedDivisionGeoJson(officeCodeWithDiv);
        if (cachedData) {
            return { data: cachedData, fromCache: true };
        }

        const myHeaders = new Headers();
        myHeaders.append("Authorization", token);
        myHeaders.append("Content-Type", "application/json");

        const raw = JSON.stringify({
            "officeCode": officeCodeWithDiv
        });

        const requestOptions = {
            method: "POST",
            headers: myHeaders,
            body: raw,
            redirect: "follow"
        };

        const response = await fetch(cfpBaseUrl + "lineComplaints/getGISMapping", requestOptions);
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Geo Json API issue!');
        }

        const result = await response.json();

        if (result.Message === "Data Not Found.") {
            throw new Error(result.Message, 'printing error message' || 'Unable to fetch stats!');
        }

        // Cache the result
        if (result.data) {
            setCachedDivisionGeoJson(officeCodeWithDiv, result.data);
        }

        return result;
    }
);

const divisionGeoJsonSlice = createSlice({
    name: 'divisionGeoJsonArray',
    initialState: initialState,
    reducers: {
        resetGeoJsonState(state) {
            state.data = null;
            state.isLoading = false;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchDivisionGeoJson.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchDivisionGeoJson.fulfilled, (state, action) => {
                state.isLoading = false;
                if (action.payload) {
                    // Handle both cached and fresh data
                    state.data = action.payload.fromCache ? action.payload.data : action.payload.data;
                } else {
                    state.data = null
                }
                state.error = null;
            })
            .addCase(fetchDivisionGeoJson.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.error.message;
            });
    },
});

export const { resetGeoJsonState } = divisionGeoJsonSlice.actions;

export default divisionGeoJsonSlice.reducer;